import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useGenerationWatch } from '@entities/Generation';
import { VISIBILITY_THRESHOLDS, type Band } from '../lib/recordVisibility';
import { createRowsTracker, type RecordGeometry } from '../lib/rowsTracker';
import { scrollRoot } from '../lib/scroll';

const band = (top: number, bottom: number): Band => ({ top, bottom, height: Math.max(0, bottom - top) });

function rectBand(el: Element): Band {
  const rect = el.getBoundingClientRect();
  return band(rect.top, rect.bottom);
}

/**
 * Живая геометрия записи: её прямоугольник, обрезка предками с `overflow` до корня (блок списка, секция, пока
 * едет её высота) и видимая полоса корня — как у IntersectionObserver с корнем-контейнером прокрутки: его
 * внутренняя область, снизу минус полоса создания.
 */
function recordGeometry(
  target: Element,
  root: HTMLElement | null,
  dockHeight: number,
  dock: HTMLElement | null,
): RecordGeometry {
  // Строка создания сверху (2026-10-28): закрыто всё выше её низа, снизу ничего не закрыто.
  const dockBottom = dock?.dataset.dockPlacement === 'top' ? dock.getBoundingClientRect().bottom : null;
  let view: Band;
  if (root) {
    const top = root.getBoundingClientRect().top + root.clientTop;
    view =
      dockBottom !== null
        ? band(Math.max(top, dockBottom), top + root.clientHeight)
        : band(top, top + root.clientHeight - dockHeight);
  } else {
    const height = document.documentElement.clientHeight;
    view = dockBottom !== null ? band(Math.max(0, dockBottom), height) : band(0, height - dockHeight);
  }
  const clips: Band[] = [];
  for (let el = target.parentElement; el && el !== root; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') clips.push(rectBand(el));
  }
  return { box: rectBand(target), clips, band: view };
}

/**
 * Какие записи на экране — для сигнала у строки (ТЗ главной § 8, «Данные»): вся запись (`<li>`) — у свёрнутой это
 * заголовок, у раскрытой ещё и тело (`isRecordVisible`). IntersectionObserver с корнем `main` и вычтенной высотой
 * полосы (`rootMargin`) будит при прокрутке, ResizeObserver — когда запись меняет высоту; решение — по живой
 * геометрии (`lib/rowsTracker.ts`): строка, которая въезжает по высоте 0 → 64 целиком внутри корня, иначе так и
 * оставалась «не на экране» (приёмка главной D, A2-01). Высоту полосы в потоке меряет ResizeObserver по `dockRef`
 * (вместе с safe-area, без абсолютных слоёв); при её смене наблюдатель пересечений пересоздаётся. Результат — в
 * сторе слежения (`observeRow`); задержку 150 мс у границы применяет модель. Запись ушла из DOM — «не на экране».
 * `onArrived` — строка доехала: впервые получила полную высоту заголовка (момент вставки для сигнала, § 8).
 */
export function useRowsInView(dockRef: RefObject<HTMLElement>, onArrived?: (id: string) => void) {
  const arrivedRef = useRef(onArrived);
  arrivedRef.current = onArrived;
  const [tracker] = useState(() =>
    createRowsTracker<Element>({
      createIntersection: (wake, dockHeight) =>
        typeof IntersectionObserver === 'undefined'
          ? null
          : new IntersectionObserver((entries) => wake(entries.map((entry) => entry.target)), {
              root: scrollRoot(dockRef.current),
              rootMargin:
                dockRef.current?.dataset.dockPlacement === 'top' ? `-${dockHeight}px 0px 0px 0px` : `0px 0px -${dockHeight}px 0px`,
              threshold: VISIBILITY_THRESHOLDS,
            }),
      createResize: (wake) =>
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver((entries) => wake(entries.map((entry) => entry.target))),
      geometry: (target, dockHeight) => recordGeometry(target, scrollRoot(dockRef.current), dockHeight, dockRef.current),
      report: (id, visible) => useGenerationWatch.getState().observeRow(id, visible),
      // Строка доехала — момент вставки для сигнала (§ 8).
      arrived: (id) => arrivedRef.current?.(id),
    }),
  );

  const [dockHeight, setDockHeight] = useState(0);
  useEffect(() => {
    const dock = dockRef.current;
    if (!dock) return;
    const measure = () => setDockHeight(Math.round(dock.offsetHeight));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(dock);
    return () => observer.disconnect();
  }, [dockRef]);

  const dockHeightRef = useRef(dockHeight);
  dockHeightRef.current = dockHeight;
  useEffect(() => {
    // Полоса уже в DOM — первые наблюдения сразу с её высотой, а не с нулём до замера.
    const dock = dockRef.current;
    tracker.start(dock ? Math.round(dock.offsetHeight) : dockHeightRef.current);
    return () => tracker.stop();
  }, [tracker, dockRef]);
  // 0 — замера ещё не было: наблюдатель уже создан с высотой полосы из DOM, лишний раз его не пересоздаём.
  useEffect(() => {
    if (dockHeight > 0) tracker.setDockHeight(dockHeight);
  }, [tracker, dockHeight]);

  // Ref-колбэк на запись — один на id, чтобы React не переподключал его на каждом рендере.
  const callbacks = useRef(new Map<string, (el: Element | null) => void>());
  const register = useCallback(
    (id: string) => {
      let callback = callbacks.current.get(id);
      if (!callback) {
        callback = (el: Element | null) => tracker.register(id, el);
        callbacks.current.set(id, callback);
      }
      return callback;
    },
    [tracker],
  );

  /** Высота полосы — сколько снизу `main` закрыто строкой создания. */
  const dockHeightNow = useCallback(() => dockRef.current?.offsetHeight ?? dockHeight, [dockRef, dockHeight]);

  return { register, dockHeightNow };
}
